import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  invoicesApi,
  InvoicesListParams,
  CreateInvoiceData,
  UpdateInvoiceData,
  RecordPaymentData,
} from '../services/invoices';

// Query keys
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (params: InvoicesListParams) => [...invoiceKeys.lists(), params] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  stats: () => [...invoiceKeys.all, 'stats'] as const,
  nextNumber: () => [...invoiceKeys.all, 'nextNumber'] as const,
};

// List invoices
export function useInvoices(params: InvoicesListParams = {}) {
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: () => invoicesApi.list(params),
  });
}

// Get single invoice
export function useInvoice(id: string) {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: () => invoicesApi.get(id),
    enabled: !!id,
  });
}

// Get invoice stats
export function useInvoiceStats() {
  return useQuery({
    queryKey: invoiceKeys.stats(),
    queryFn: () => invoicesApi.getStats(),
  });
}

// Get next invoice number
export function useNextInvoiceNumber() {
  return useQuery({
    queryKey: invoiceKeys.nextNumber(),
    queryFn: () => invoicesApi.getNextNumber(),
  });
}

// Create invoice
export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInvoiceData) => invoicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.nextNumber() });
    },
  });
}

// Create invoice from bookings
export function useCreateInvoiceFromBookings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingIds, dueDate }: { bookingIds: string[]; dueDate: string }) =>
      invoicesApi.createFromBookings(bookingIds, dueDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

// Update invoice
export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInvoiceData }) =>
      invoicesApi.update(id, data),
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.setQueryData(invoiceKeys.detail(invoice._id), invoice);
    },
  });
}

// Send invoice
export function useSendInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoicesApi.send(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
      queryClient.setQueryData(invoiceKeys.detail(result.invoice._id), result.invoice);
    },
  });
}

// Resend invoice email
export function useResendInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, email }: { id: string; email?: string }) =>
      invoicesApi.resend(id, email),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(variables.id) });
    },
  });
}

// Record payment
export function useRecordPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RecordPaymentData }) =>
      invoicesApi.recordPayment(id, data),
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
      queryClient.setQueryData(invoiceKeys.detail(invoice._id), invoice);
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

// Cancel invoice
export function useCancelInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason, note }: { id: string; reason: 'client_requested' | 'duplicate' | 'incorrect_amount' | 'service_not_delivered' | 'payment_arrangement' | 'other'; note?: string }) =>
      invoicesApi.cancel(id, reason, note),
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
      queryClient.setQueryData(invoiceKeys.detail(invoice._id), invoice);
    },
  });
}

// Extend due date
export function useExtendDueDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dueDate, reason }: { id: string; dueDate: string; reason?: string }) =>
      invoicesApi.extendDueDate(id, dueDate, reason),
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
      queryClient.setQueryData(invoiceKeys.detail(invoice._id), invoice);
    },
  });
}

// Delete invoice
export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}
